/** Desktop (Tauri) integration. In the browser `isDesktop()` is false and nothing here is called. */
import { invoke, convertFileSrc, isTauri as tauriCheck } from "@tauri-apps/api/core";

export const isDesktop = (): boolean => {
  try { return tauriCheck(); } catch { return false; }
};

/**
 * The public website — invite links from the desktop app point here so guests need nothing installed.
 * Move it with `pnpm set-address <url>` (tools/set-address.mjs): that also updates the Worker, the docs and the list below.
 */
export const PUBLIC_WEB_URL = ((import.meta.env?.VITE_PUBLIC_URL as string | undefined) || "https://app.mc-planner.com/").replace(/\/?$/, "/");
/**
 * Earlier addresses of the public website/server. Invite links, saved settings and host keys that still name one of
 * them are treated as PUBLIC_WEB_URL (lib/share.ts serverOrigin). tools/set-address.mjs adds the old address here.
 */
export const RETIRED_SERVERS: string[] = ["https://mc-planner-server.mc-planner.workers.dev", "https://app.mc-planner.workers.dev"];

export interface FoundJar { label: string; path: string; version: string; size: number }
export interface FoundWorld { name: string; path: string; instance: string; modified: number; icon?: string | null }
export interface FoundMinecraft {
  jars: FoundJar[];
  worlds: FoundWorld[];
  /** launcher folders that were searched */
  searched: string[];
}
export interface WorldStamp { files: number; bytes: number; newest: number }

// ---------- folders the user added (worlds outside the launchers, e.g. D:\Backups) ----------

const FOLDERS_KEY = "mcp-desktop-folders";

export function extraFolders(): string[] {
  try { return JSON.parse(localStorage.getItem(FOLDERS_KEY) ?? "[]") as string[]; } catch { return []; }
}

export function addExtraFolder(path: string) {
  const p = path.trim().replace(/^"(.*)"$/, "$1").replace(/[\\/]+$/, "");
  if (!p) return;
  const list = [p, ...extraFolders().filter((x) => x.toLowerCase() !== p.toLowerCase())].slice(0, 20);
  try { localStorage.setItem(FOLDERS_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

export function removeExtraFolder(path: string) {
  try { localStorage.setItem(FOLDERS_KEY, JSON.stringify(extraFolders().filter((x) => x !== path))); } catch { /* ignore */ }
}

// ---------- native commands (apps/desktop/src-tauri/src/lib.rs) ----------

export async function findMinecraft(): Promise<FoundMinecraft> {
  return invoke<FoundMinecraft>("find_minecraft", { extra: extraFolders() });
}

/**
 * Links that open the desktop app (`mcplanner://…`, e.g. the sign-in callback). Calls `cb` for a link that launched
 * the app and for every later one (a second app instance forwards its link here — tauri-plugin-single-instance).
 */
export function onDeepLink(cb: (url: string) => void) {
  if (!isDesktop()) return;
  void import("@tauri-apps/plugin-deep-link")
    .then(async ({ onOpenUrl, getCurrent }) => {
      try { (await getCurrent())?.forEach(cb); } catch { /* not started from a link */ }
      await onOpenUrl((urls) => urls.forEach(cb));
    })
    .catch((e) => console.warn("deep links unavailable", e));
}

/** Open an https:// link in the default browser (src-tauri `open_url`). */
export async function openExternal(url: string) {
  await invoke("open_url", { url });
}

export function fileUrl(path: string) {
  return convertFileSrc(path);
}

/** Files of a world folder as asset-protocol URLs (read in place, no zipping). */
export async function worldFileUrls(root: string) {
  const files = await invoke<{ path: string; size: number; modified: number }[]>("list_world_files", { root });
  const sep = root.includes("\\") ? "\\" : "/";
  const base = root.replace(/[\\/]+$/, "");
  // prefix with the folder name so detectWorlds() sees "<world>/level.dat"
  const top = base.split(/[\\/]/).pop() || "world";
  return files.map((f) => ({
    path: `${top}/${f.path}`,
    // ?v= makes a region file that Minecraft rewrote a new URL (the webview may cache the old one)
    url: `${convertFileSrc(base + sep + f.path.split("/").join(sep))}?v=${f.modified}`,
  }));
}

export async function worldStamp(root: string): Promise<WorldStamp> {
  return invoke<WorldStamp>("world_stamp", { root });
}

export async function readLocalFile(path: string): Promise<Blob> {
  await invoke("allow_path", { path }).catch(() => {});
  const r = await fetch(convertFileSrc(path), { cache: "no-store" });
  if (!r.ok) throw new Error(`Could not read ${path}`);
  return r.blob();
}

/** Save into Documents/MC Planner (never overwrites). Returns the full path. */
export async function saveTextFile(name: string, contents: string): Promise<string> {
  return invoke<string>("save_text_file", { name, contents });
}

export async function revealPath(path: string) {
  return invoke("reveal_path", { path });
}
